import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../App";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PromotionCard from "../components/PromotionCard";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Clock, TrendingUp, Play, CheckCircle2, Lock } from "lucide-react";
import { resolveImageUrl } from "../lib/images";
import { toast } from "sonner";
import { formatDuration } from "../lib/format";

const CourseDetail = () => {
  const { id } = useParams();
  const { user, API } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolled, setEnrolled] = useState(false);
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    fetchCourse();
    if (user) {
      checkEnrollment();
    }
  }, [id, user]);

  const fetchCourse = async () => {
    try {
      const response = await axios.get(`${API}/courses/${id}`);
      setCourse(response.data);
    } catch (error) {
      console.error("Error fetching course:", error);
      toast.error("Error al cargar el curso");
    } finally {
      setLoading(false);
    }
  };

  const checkEnrollment = async () => {
    try {
      const response = await axios.get(`${API}/my-courses`);
      const isEnrolled = response.data.some((c) => c.id === id);
      setEnrolled(isEnrolled);
    } catch (error) {
      console.error("Error checking enrollment:", error);
    }
  };

  const handleEnroll = async () => {
    if (!user) {
      navigate("/auth");
      return;
    }

    if (course.tier === "PRO" && user.subscription_status !== "pro") {
      toast.error("Este curso requiere suscripción PRO");
      navigate("/contaflix-pro");
      return;
    }

    setEnrolling(true);
    try {
      await axios.post(`${API}/enrollments`, { course_id: id });
      toast.success("¡Te has inscrito al curso!");
      setEnrolled(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al inscribirse");
    } finally {
      setEnrolling(false);
    }
  };

  const handleStartCourse = () => {
    navigate(`/player/${id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="text-white text-xl">Cargando...</div>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="text-white text-xl">Curso no encontrado</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Header />

      {/* Hero Section */}
      <div className="relative h-[50vh] md:h-[60vh]">
        <div className="absolute inset-0">
          <img src={resolveImageUrl(course.image_url)} alt={course.title} width={1280} height={720} loading="lazy" decoding="async" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 h-full flex items-end pb-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Badge
                className={
                  course.tier === "PRO"
                    ? "bg-[#E50914] text-white border-0 text-sm"
                    : "bg-white/10 text-white border-white/20 text-sm"
                }
              >
                {course.tier}
              </Badge>
              <Badge className="bg-white/10 text-white border-white/20 text-sm">
                {course.level}
              </Badge>
              <Badge className="bg-white/10 text-white border-white/20 text-sm">
                {course.category}
              </Badge>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-4">
              {course.title}
            </h1>
            <div className="flex items-center gap-6 text-white/80">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span>{formatDuration(course.duration_hours)}</span>
              </div>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                <span>{course.category}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        {course.tier === "PRO" && (!user || user.subscription_status !== "pro") && (
          <div className="mb-8">
            <PromotionCard />
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <div>
              <h2 className="text-2xl font-bold text-white mb-4">Descripción</h2>
              <p className="text-white/80 leading-relaxed">{course.description}</p>
            </div>

            {/* What you'll learn */}
            {course.what_you_will_learn && course.what_you_will_learn.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Qué aprenderás</h2>
                <ul className="space-y-3">
                  {course.what_you_will_learn.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#E50914] flex-shrink-0 mt-0.5" />
                      <span className="text-white/80">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Modules */}
            {course.modules && course.modules.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Módulos del Curso</h2>
                <Accordion type="single" collapsible className="space-y-3">
                  {course.modules.map((module, index) => (
                    <AccordionItem
                      key={module.id}
                      value={`module-${index}`}
                      className="bg-card/50 backdrop-blur-md border border-white/10 rounded-lg px-6"
                    >
                      <AccordionTrigger className="text-white font-semibold hover:text-[#E50914] transition-colors">
                        <div className="flex items-center gap-3">
                          <span>Módulo {module.order}: {module.title}</span>
                          <span className="text-sm text-white/60">({module.lessons.length} lecciones)</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <ul className="space-y-2 mt-2">
                          {module.lessons.map((lesson) => (
                            <li
                              key={lesson.id}
                              className="flex items-center gap-3 text-white/70 py-2 border-b border-white/5 last:border-0"
                            >
                              <Play className="w-4 h-4 text-[#E50914]" />
                              <span>{lesson.title}</span>
                              <span className="ml-auto text-sm text-white/50">
                                {lesson.duration_minutes} min
                              </span>
                            </li>
                          ))}
                        </ul>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}

            {/* Requirements */}
            {course.requirements && course.requirements.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Requisitos</h2>
                <ul className="space-y-2">
                  {course.requirements.map((req, index) => (
                    <li key={index} className="text-white/70 flex items-start gap-2">
                      <span className="text-[#E50914] mt-1">•</span>
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div>
            <div className="bg-card/50 backdrop-blur-md border border-white/10 rounded-lg p-6 sticky top-24">
              {course.tier === "PRO" && user && user.subscription_status !== "pro" ? (
                <div className="text-center space-y-4">
                  <Lock className="w-12 h-12 text-[#E50914] mx-auto" />
                  <h3 className="text-xl font-bold text-white">Contenido Premium</h3>
                  <p className="text-white/70 text-sm">
                    Este curso requiere una suscripción ContaFLIX PRO para acceder.
                  </p>
                  <Button
                    onClick={() => navigate("/contaflix-pro")}
                    className="w-full bg-[#E50914] hover:bg-[#E50914]/90 text-white shadow-[0_0_20px_rgba(229,9,20,0.4)] hover:shadow-[0_0_30px_rgba(229,9,20,0.6)] transition-all duration-300 font-semibold"
                    data-testid="upgrade-to-pro-btn"
                  >
                    Actualizar a PRO
                  </Button>
                </div>
              ) : enrolled ? (
                <div className="text-center space-y-4">
                  <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto" />
                  <h3 className="text-xl font-bold text-white">Ya estás inscrito</h3>
                  <Button
                    onClick={handleStartCourse}
                    className="w-full bg-[#E50914] hover:bg-[#E50914]/90 text-white shadow-[0_0_20px_rgba(229,9,20,0.4)] hover:shadow-[0_0_30px_rgba(229,9,20,0.6)] transition-all duration-300 font-semibold"
                    data-testid="continue-course-btn"
                  >
                    <Play className="w-5 h-5 mr-2 fill-white" />
                    Continuar Curso
                  </Button>
                </div>
              ) : (
                <div className="text-center space-y-4">
                  <h3 className="text-xl font-bold text-white">Comienza ahora</h3>
                  <Button
                    onClick={handleEnroll}
                    disabled={enrolling}
                    className="w-full bg-[#E50914] hover:bg-[#E50914]/90 text-white shadow-[0_0_20px_rgba(229,9,20,0.4)] hover:shadow-[0_0_30px_rgba(229,9,20,0.6)] transition-all duration-300 font-semibold"
                    data-testid="enroll-course-btn"
                  >
                    {enrolling ? "Inscribiendo..." : "Empezar Curso"}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CourseDetail;
